import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MainLayout from '../../components/Layout/MainLayout';
import Table, { Column } from '../../components/Table/Table';
import TabBar from '../../components/Tabs/TabBar';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import ErrorModal from '../../components/Modals/ErrorModal';
import AddUserModal from './AddUserModal';
import { formatDate } from '../../utils/dataMappers';
import { mapRoleToDisplay } from '../../utils/roleMapper';
import { selectOutlets, selectUserRole } from '../../store/selectors/authSelectors';
import { selectPocs } from '../../store/selectors/adminSelectors';
import styles from './AdminPage.module.css';
import { CreateUserPayload, Job } from '../../types';
import { User, Employee } from '../../types';
import FilterBar from '../../components/Filters/FilterBar';
import { useAdminData } from './hooks/useAdminData';
import { useAdminActions } from './hooks/useAdminActions';

// Utility function for mapping job status
const mapJobStatusToUi = (status: string) => {
  const map: Record<string, string> = { OPEN: 'Open', CLOSED: 'Closed', PAUSED: 'Paused' };
  return map[status] || 'Open';
};

interface AdminPageProps {
  user?: User | null;
  onLogout: () => void;
}

const AdminPage: React.FC<AdminPageProps> = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const outlets = useSelector(selectOutlets);
  const pocs = useSelector(selectPocs);
  const userRole = useSelector(selectUserRole);
  const [activeTab, setActiveTab] = useState<'users' | 'jobs'>('users');

  // Custom Hooks
  const { jobs, users, isLoading, refreshUsers, refreshJobs, setJobs } = useAdminData();
  const { handleReassignPoc, handleAddUser, handleDeletePoc } = useAdminActions(refreshUsers, refreshJobs, setJobs);

  // Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});

  // State for modals
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<Employee | null>(null);
  const [addUserError, setAddUserError] = useState('');
  const [errorModalMessage, setErrorModalMessage] = useState('');

  // Reset filters when tab changes
  useEffect(() => {
    setSearchTerm('');
    setFilters({});
  }, [activeTab]);

  const handleRowClick = (job: Job) => {
    navigate(`/ats/${job.job_id}`, { state: { job } });
  };

  const onAddUserSubmit = async (newUser: CreateUserPayload) => {
    setAddUserError('');
    const result = await handleAddUser(newUser);
    if (result.success) {
      setIsAddUserModalOpen(false);
    } else {
      setAddUserError(result.error || 'Failed to create user');
    }
  };

  const onDeletePocConfirm = async () => {
    if (!userToDelete) return;
    const result = await handleDeletePoc(userToDelete.id);

    if (result.success) {
      setUserToDelete(null);
    } else {
      if (result.errorCode === 'NO_MANAGER_FOR_OUTLET') {
        setUserToDelete(null);
        setErrorModalMessage('Cannot delete user: At least one manager is required for each outlet.');
      } else if (result.errorCode === 'NO_ACTIVE_POC_FOR_JOB') {
        setUserToDelete(null);
        setErrorModalMessage('Cannot delete user: At least one active POC is required for each job.');
      }
    }
  };

  // Filter Configurations
  const filterConfigs = useMemo(() => {
    const outletOptions = outlets.map((o) => ({ label: o.outlet_name, value: o.outlet_id }));

    if (activeTab === 'users') {
      const uniqueRoles = Array.from(new Set(users.map((u) => u.role))).filter(Boolean);
      return [
        {
          key: 'outlet_id',
          label: 'All Outlets',
          options: outletOptions,
        },
        {
          key: 'role',
          label: 'All Roles',
          options: uniqueRoles
            .filter((r) => r !== 'ORG_ADMIN')
            .map((r) => ({ label: mapRoleToDisplay(r), value: r })),
        },
      ];
    } else {
      return [
        {
          key: 'status',
          label: 'All Statuses',
          options: ['OPEN', 'CLOSED'].map((s) => ({
            label: mapJobStatusToUi(s),
            value: s,
          })),
        },
        {
          key: 'outlet_id',
          label: 'All Outlets',
          options: outletOptions,
        },
      ];
    }
  }, [activeTab, outlets, users]);

  // Define columns for the reusable table component
  const jobColumns = useMemo<Column<Job>[]>(() => {
    const cols: Column<Job>[] = [
      {
        Header: 'Job Title',
        accessor: 'title',
        Cell: ({ row }) => <span data-testid={`job-title-${row.job_id}`}>{row.title}</span>,
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ row }) => <span>{mapJobStatusToUi(row.status)}</span>,
        sortable: true,
      },
      {
        Header: 'Posting Date',
        accessor: 'created_at',
        Cell: ({ row }) => <span>{formatDate(row.created_at)}</span>,
        sortable: true,
      },
      {
        Header: 'Assigned POC',
        accessor: 'poc_id', // Add accessor for sorting if needed
        Cell: ({ row }) => {
          const currentPocId = row.poc_id || '';
          // Filter to show only POCs with role 'OUTLET_POC' and belonging to the same outlet
          const outletPocs = pocs.filter((poc) => poc.outlet_id === row.outlet_id);

          return (
            <select
              value={currentPocId}
              onChange={(e) => {
                e.stopPropagation();
                const newPocId = e.target.value;
                if (newPocId) {
                  handleReassignPoc(row.job_id, newPocId);
                }
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              style={{ fontSize: '14px', padding: '6px 8px', minWidth: '150px', cursor: 'pointer' }}
              data-testid={`poc-select-${row.job_id}`}
            >
              {outletPocs.map((poc) => (
                <option key={poc.id} value={poc.id}>
                  {poc.name}
                </option>
              ))}
            </select>
          );
        },
      },
    ];

    if (userRole === 'ORG_ADMIN') {
      cols.push({
        Header: 'Outlet Name',
        accessor: 'outlet_name', // Assuming outlet_name is available in job object or derived
        Cell: ({ row }) => {
          // If outlet_name is not directly in row, find it from outlets list using outlet_id
          const outlet = outlets.find((o) => o.outlet_id === row.outlet_id);
          return <span data-testid={`job-outlet-${row.job_id}`}>{outlet?.outlet_name || row.outlet_name || '—'}</span>;
        },
      });
    }

    return cols;
  }, [pocs, handleReassignPoc, userRole, outlets]);

  const userColumns = useMemo<Column<Employee>[]>(
    () => [
      { Header: 'Name', accessor: 'name' },
      { Header: 'Email Address', accessor: 'email' },
      {
        Header: 'Roles',
        accessor: 'role',
        Cell: ({ row }) => {
          const displayRole = mapRoleToDisplay(row.role);
          return <span className={styles.roleBadge} data-testid={`user-role-${row.id}`}>{displayRole || '—'}</span>;
        },
      },
      {
        Header: 'Outlet Name',
        accessor: 'outlet_id',
        Cell: ({ row }) => {
          const outlet = outlets.find((out) => out.outlet_id === row.outlet_id);
          return <span data-testid={`user-outlet-${row.id}`}>{outlet?.outlet_name || '—'}</span>;
        },
      },
      {
        Header: 'Actions',
        accessor: 'id',
        sortable: false,
        Cell: ({ row }) => (
          <button
            className="button-primary"
            onClick={(e) => {
              e.stopPropagation();
              setUserToDelete(row);
            }}
            data-testid={`delete-user-${row.id}`}
          >
            Delete
          </button>
        ),
      },
    ],
    [outlets]
  );

  const processedJobs = useMemo(() => {
    return jobs.map((job) => {
      const outlet = outlets.find((o) => o.outlet_id === job.outlet_id);
      return {
        ...job,
        outlet_name: outlet?.outlet_name || job.outlet_name || '',
      };
    });
  }, [jobs, outlets]);

  // Filtered Users
  const displayedUsers = useMemo(() => {
    return users.filter((u) => {
      // Don't show the current user in the manage users table
      if (u.id === user?.id) return false;

      // Search
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const outlet = outlets.find((o) => o.outlet_id === u.outlet_id);
        const outletName = outlet?.outlet_name?.toLowerCase() || '';

        const matchesSearch =
          u.name.toLowerCase().includes(term) ||
          u.email.toLowerCase().includes(term) ||
          outletName.includes(term);

        if (!matchesSearch) return false;
      }

      // Filters
      if (filters.outlet_id && filters.outlet_id !== 'all' && u.outlet_id !== filters.outlet_id)
        return false;
      if (filters.role && filters.role !== 'all' && u.role !== filters.role) return false;

      return true;
    });
  }, [users, searchTerm, filters, outlets, user?.id]);

  // Filtered Jobs
  const displayedJobs = useMemo(() => {
    return processedJobs.filter((j) => {
      // Search
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesSearch =
          j.title.toLowerCase().includes(term) ||
          j.status.toLowerCase().includes(term) ||
          (j.outlet_name || '').toLowerCase().includes(term);

        if (!matchesSearch) return false;
      }

      // Filters
      if (filters.status && filters.status !== 'all' && j.status !== filters.status) return false;
      if (
        filters.outlet_id &&
        filters.outlet_id !== 'all' &&
        j.outlet_id !== filters.outlet_id
      )
        return false;

      return true;
    });
  }, [processedJobs, searchTerm, filters]);

  if (isLoading)
    return (
      <MainLayout user={user} onLogout={onLogout}>
        <div className={styles.adminPage} data-testid="admin-page">
          <header className={styles.header}>
            <h1 className={styles.title}>Admin Panel</h1>
          </header>

          <div className={styles.tabs} data-testid="admin-loading">
            <div className={styles.tabSkeleton}></div>
            <div className={styles.tabSkeleton}></div>
          </div>

          <div className={styles.tableSkeleton}>
            {[...Array(5)].map((_, index) => (
              <div key={index} className={styles.skeletonRow}></div>
            ))}
          </div>
        </div>
      </MainLayout>
    );

  return (
    <MainLayout user={user} onLogout={onLogout}>
      <div className={styles.adminPage} data-testid="admin-page">
        <header className={styles.header}>
          <h1 className={styles.title} data-testid="admin-title">Admin Panel</h1>
          {activeTab === 'users' && (
            <button className={styles.primaryButton} onClick={() => setIsAddUserModalOpen(true)} data-testid="add-user-button">
              + Add New User
            </button>
          )}
        </header>

        <FilterBar
          key={activeTab}
          filters={filterConfigs}
          onSearchChange={setSearchTerm}
          onFilterChange={(newFilters) => setFilters((prev) => ({ ...prev, ...newFilters }))}
        />

        <TabBar
          tabs={[
            { id: 'users', label: 'Manage Users' },
            { id: 'jobs', label: 'All Jobs' },
          ]}
          activeTab={activeTab}
          onTabChange={(id) => setActiveTab(id as 'users' | 'jobs')}
        />

        {activeTab === 'users' ? (
          <Table columns={userColumns} data={displayedUsers} />
        ) : (
          <Table columns={jobColumns} data={displayedJobs} onRowClick={handleRowClick} />
        )}
      </div>
      <AddUserModal
        isOpen={isAddUserModalOpen}
        onClose={() => {
          setIsAddUserModalOpen(false);
          setAddUserError(''); // Clear error when closing
        }}
        onAddUser={onAddUserSubmit}
        error={addUserError}
        isSubmitting={false}
      />

      <ConfirmationModal
        isOpen={!!userToDelete}
        title="Delete User?"
        confirmText="Delete"
        onConfirm={onDeletePocConfirm}
        onCancel={() => {
          setUserToDelete(null);
        }}
      >
        Are you sure you want to delete {userToDelete?.name}? This action cannot be undone.
      </ConfirmationModal>

      <ErrorModal
        isOpen={!!errorModalMessage}
        message={errorModalMessage}
        onClose={() => setErrorModalMessage('')}
      />
    </MainLayout>
  );
};

export default AdminPage;
